/**
 * PluginEmbedHost — renders any registered embeddable component with its
 * OWNING plugin's context, wherever it is embedded (workspace builder,
 * dashboard, …). Registered with the SDK as the renderer behind
 * `<EmbeddedPluginComponent>`.
 *
 *   EmbedFrame → EmbeddedRouter (views) → PluginUIProvider (owner) →
 *   EmbedInstanceContext (bridge) → error boundary → Suspense → component
 *
 * The component receives the same props a page gets (api, apiClient,
 * navigate, userId, pluginId, events; views also routeParams), plus the
 * consumer's props — which can never override those context keys.
 * Embeds deliberately skip PageActionProvider/PageActionLayout and never
 * count crashes toward auto-disabling the owning plugin.
 */

import React, { Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  EmbeddableComponentDescriptor,
  EmbeddedPluginComponentProps,
  EmbedInstanceController,
  EmbedMode,
  EmbedNavigationDecision,
  EmbedNavigationRequest,
} from '@neuro/plugin-sdk/ui';
import { PluginUIProvider } from '@neuro/plugin-ui';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../registries/permission-registry';
import { usePluginLogStore } from '../sandbox/plugin-log-store';
import { usePluginStabilityStore } from '../sandbox/plugin-stability-store';
import { EmbedFrame } from '../embed/EmbedFrame';
import { EmbeddedRouter } from '../embed/EmbeddedRouter';
import { EmbedInstance } from '../embed/embed-instance-controller';
import { getEmbedSdk } from '../embed/sdk-embed';
import { reloadForChunkLoadError } from '../../utils/chunkLoadRecovery';
import {
  buildPluginHostContext,
  getPluginEventsApi,
  getScopedPluginApi,
  resolvePluginCapabilities,
} from '../runtime/plugin-runtime-context';

/** Context props the embedding surface may not override. */
export const RESERVED_EMBED_PROPS = ['api', 'apiClient', 'navigate', 'userId', 'pluginId', 'events', 'pagePath', 'routeParams'] as const;

let instanceCounter = 0;

function stripReserved(props: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!props) return {};
  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if ((RESERVED_EMBED_PROPS as readonly string[]).includes(key)) {
      if (import.meta.env?.DEV) console.warn(`[PluginEmbedHost] ignoring reserved prop "${key}"`);
      continue;
    }
    clean[key] = value;
  }
  return clean;
}

function Placeholder({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="nc-embed-placeholder" role="note">
      <strong>{title}</strong>
      {detail && <span>{detail}</span>}
    </div>
  );
}

// ── Error boundary that never counts toward crash auto-disable ──────────

interface BoundaryProps {
  pluginId: string;
  label: string;
  onError?: (error: Error) => void;
  onRetry: () => void;
  children: React.ReactNode;
}

class EmbedErrorBoundary extends React.Component<BoundaryProps, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const { pluginId, label } = this.props;
    if (reloadForChunkLoadError(error)) return;
    // Logged and recorded as an event — deliberately NOT recordCrash: a
    // misconfigured embed retried a few times must not disable the plugin.
    usePluginStabilityStore.getState().recordEvent(pluginId, 'ui-error', `Embedded "${label}": ${error.message}`, {
      componentStack: info.componentStack ?? undefined,
    });
    usePluginLogStore.getState().appendLog(pluginId, 'error', `Embedded UI error (${label}): ${error.message}`, 'PluginEmbedHost');
    console.error(`[PluginEmbedHost] "${label}" (${pluginId}) failed`, error, info.componentStack);
    this.props.onError?.(error);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="nc-embed-placeholder" role="alert">
        <strong>{this.props.label} failed to load</strong>
        <span>{this.state.error.message}</span>
        <button
          type="button"
          className="nc-btn nc-btn--secondary nc-btn--sm"
          onClick={() => { this.setState({ error: null }); this.props.onRetry(); }}
        >
          Retry
        </button>
      </div>
    );
  }
}

// ── Inner runtime: inside the (embedded) router ─────────────────────────

interface RuntimeProps {
  descriptor: EmbeddableComponentDescriptor;
  Component: React.ComponentType<any>;
  consumerProps: Record<string, unknown>;
  instance: EmbedInstance;
  mode: EmbedMode;
  fallbackRouteParams: Record<string, string>;
  /** Navigation for non-router callers (props.navigate / hostContext.navigate). */
  navigatePolicy: (path: string) => void;
  isolated: boolean;
  loadingFallback?: React.ReactNode;
}

function EmbedRuntime({
  descriptor, Component, consumerProps, instance, mode, fallbackRouteParams, navigatePolicy, isolated, loadingFallback,
}: RuntimeProps) {
  const sdk = getEmbedSdk()!;
  const routerNavigate = useNavigate();
  const location = useLocation();
  const userId = useAuthStore((s) => s.userId) ?? 'unknown';
  const { pluginId } = descriptor;

  // Inside an isolated router, navigate through it (in-frame for the
  // plugin's routes, policy otherwise); without isolation use the policy.
  const navigate = useCallback(
    (path: string) => (isolated ? routerNavigate(path) : navigatePolicy(path)),
    [isolated, navigatePolicy, routerNavigate],
  );

  const api = getScopedPluginApi(pluginId);
  const events = getPluginEventsApi(pluginId);
  const capabilities = resolvePluginCapabilities(pluginId, 'PluginEmbedHost');
  const hostContext = useMemo(() => buildPluginHostContext({
    pluginId, userId, navigate, api, events,
    embed: { instanceId: instance.instanceId, componentId: descriptor.id, mode },
  }), [api, descriptor.id, events, instance.instanceId, mode, navigate, pluginId, userId]);

  const routeParams = useMemo(() => {
    if (descriptor.kind !== 'view') return undefined;
    for (const pattern of descriptor.route?.internalPatterns ?? []) {
      const params = sdk.matchRoutePattern(pattern, location.pathname);
      if (params) return params;
    }
    return fallbackRouteParams;
  }, [descriptor, fallbackRouteParams, location.pathname, sdk]);

  // Existing callback props (e.g. onSelect) become events, without plugin changes.
  const forwarders = useMemo(() => {
    const result: Record<string, (...args: unknown[]) => void> = {};
    for (const event of descriptor.events ?? []) {
      if (!event.prop) continue;
      const consumerHandler = consumerProps[event.prop];
      result[event.prop] = (...args: unknown[]) => {
        const payload = Array.isArray(event.args)
          ? Object.fromEntries(event.args.map((name, i) => [name, args[i]]))
          : event.args === 'array' ? args : args[0];
        instance.emit(event.name, payload);
        if (typeof consumerHandler === 'function') (consumerHandler as (...a: unknown[]) => void)(...args);
      };
    }
    return result;
  }, [consumerProps, descriptor.events, instance]);

  const props: Record<string, unknown> = {
    ...consumerProps,
    ...forwarders,
    config: consumerProps,
    api,
    apiClient: api,
    navigate,
    userId,
    pluginId,
    events,
  };
  if (routeParams) {
    Object.assign(props, routeParams, { routeParams, pagePath: descriptor.route?.pattern });
  }

  const content = (
    <sdk.EmbedInstanceContext.Provider value={instance.bridge}>
      <Suspense fallback={loadingFallback ?? <Placeholder title="Loading…" />}>
        <Component {...props} />
      </Suspense>
    </sdk.EmbedInstanceContext.Provider>
  );
  if (descriptor.embed?.contextMode === 'none') return content;
  return (
    <PluginUIProvider pluginId={pluginId} version="1.0.0" capabilities={capabilities} hostContext={hostContext}>
      {content}
    </PluginUIProvider>
  );
}

// ── Host ─────────────────────────────────────────────────────────────────

export function PluginEmbedHost(props: EmbeddedPluginComponentProps) {
  const sdk = getEmbedSdk();
  if (!sdk) return <>{props.unavailableFallback ?? <Placeholder title="Embedding unavailable" />}</>;
  return <EmbedHostInner sdk={sdk} {...props} />;
}

function EmbedHostInner({ sdk, ...props }: EmbeddedPluginComponentProps & { sdk: NonNullable<ReturnType<typeof getEmbedSdk>> }) {
  const descriptor = sdk.useEmbeddableComponent(props.componentId);
  const hostNavigate = useNavigate();
  const { checkPermissions, isLoading: permissionsLoading } = usePermissions();
  const mode: EmbedMode = props.mode ?? 'run';
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const [generation, setGeneration] = useState(0);
  const navigateRef = useRef<((path: string) => void) | null>(null);

  const instanceId = useMemo(() => props.instanceId ?? `embed-${++instanceCounter}`, [props.instanceId]);
  const instance = useMemo(
    () => new EmbedInstance(instanceId, descriptor?.id ?? props.componentId, () => modeRef.current),
    [descriptor?.id, instanceId, props.componentId],
  );

  // Keep the latest consumer callbacks without recreating the instance.
  const propsRef = useRef(props);
  propsRef.current = props;
  instance.callbacks = {
    onEvent: (event) => propsRef.current.onEvent?.(event),
    onStateChange: (state) => propsRef.current.onStateChange?.(state),
    navigate: (path) => navigateRef.current?.(path),
    remount: () => setGeneration((g) => g + 1),
  };
  useImperativeHandle(props.controllerRef as React.Ref<EmbedInstanceController> | undefined, () => instance.controller, [instance]);

  const isInternal = useCallback(
    (path: string) => !!descriptor?.route?.internalPatterns.some((p) => sdk.matchRoutePattern(p, path)),
    [descriptor, sdk],
  );

  const decide = useCallback((request: EmbedNavigationRequest): EmbedNavigationDecision => {
    const explicit = propsRef.current.onNavigate?.(request);
    if (explicit) return explicit;
    const policy = propsRef.current.navigation ?? descriptor?.embed?.navigation ?? 'auto';
    if (request.internal) return policy === 'host' ? 'host' : 'internal';
    if (policy === 'host') return 'host';
    if (policy === 'auto') return modeRef.current === 'run' ? 'host' : 'block';
    return 'block';
  }, [descriptor]);

  /** Navigation leaving the plugin's routes (or any navigation without an isolated router). */
  const handleExternal = useCallback((path: string, replace: boolean) => {
    const request: EmbedNavigationRequest = { path, internal: false, replace, componentId: instance.componentId, instanceId };
    instance.emit('navigate', { path });
    if (decide(request) === 'host') hostNavigate(path, { replace });
  }, [decide, hostNavigate, instance, instanceId]);

  const navigatePolicy = useCallback((path: string) => {
    const internal = isInternal(path);
    const request: EmbedNavigationRequest = { path, internal, replace: false, componentId: instance.componentId, instanceId };
    const decision = decide(request);
    if (decision === 'internal' && navigateRef.current) navigateRef.current(path);
    else {
      instance.emit('navigate', { path });
      if (decision === 'host') hostNavigate(path);
    }
  }, [decide, hostNavigate, instance, instanceId, isInternal]);

  const onLocationChange = useCallback((path: string) => instance.setState({ $location: path }), [instance]);

  useEffect(() => () => { navigateRef.current = null; }, []);

  if (!descriptor || descriptor.status !== 'ready' || !descriptor.embeddable) {
    return (
      <>
        {props.unavailableFallback ?? (
          <Placeholder
            title="Component unavailable"
            detail={descriptor ? `${descriptor.title} can’t be embedded` : `${props.componentId} — plugin not installed or disabled`}
          />
        )}
      </>
    );
  }
  if (!permissionsLoading && descriptor.requiredPermissions.length && !checkPermissions(descriptor.requiredPermissions)) {
    return <Placeholder title="No access" detail={`You don’t have permission to view ${descriptor.title}.`} />;
  }

  const suppliedParams = props.route?.params ?? {};
  const missing = (descriptor.route?.params ?? []).filter((p) => !suppliedParams[p]);
  if (missing.length && !descriptor.embed?.paramsOptional) {
    return <Placeholder title={`${descriptor.title} needs configuration`} detail={`Set the route parameter${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`} />;
  }

  const Component = sdk.registry.getLazyComponent(descriptor.id);
  if (!Component) return <Placeholder title="Component unavailable" detail={descriptor.title} />;

  const consumerProps = stripReserved(props.props);
  const isolated = descriptor.embed?.isolateRouter !== false;
  const basePath = descriptor.route
    ? (sdk.fillRoutePattern(descriptor.route.pattern, suppliedParams) ?? descriptor.route.pattern)
    : `/${descriptor.pluginId}`;
  const initialPath = (props.route?.path && isInternal(props.route.path) ? props.route.path : basePath) + (props.route?.search ?? '');

  const runtime = (
    <EmbedRuntime
      descriptor={descriptor}
      Component={Component}
      consumerProps={consumerProps}
      instance={instance}
      mode={mode}
      fallbackRouteParams={suppliedParams}
      navigatePolicy={navigatePolicy}
      isolated={isolated}
      loadingFallback={props.loadingFallback}
    />
  );

  return (
    <EmbedFrame kind={descriptor.kind} className={props.className} style={props.style}>
      <EmbedErrorBoundary
        key={`${descriptor.generation}:${generation}`}
        pluginId={descriptor.pluginId}
        label={descriptor.title}
        onError={props.onError}
        onRetry={() => setGeneration((g) => g + 1)}
      >
        {isolated ? (
          <EmbeddedRouter
            initialPath={initialPath}
            internalPatterns={descriptor.route?.internalPatterns ?? []}
            isInternal={isInternal}
            onExternal={handleExternal}
            onLocationChange={onLocationChange}
            navigateRef={navigateRef}
          >
            {runtime}
          </EmbeddedRouter>
        ) : runtime}
      </EmbedErrorBoundary>
    </EmbedFrame>
  );
}
